import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Col, Row } from "reactstrap";
import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/Layout/PageTitle";
import InputComponent from "../../components/InputComponent";
import ButtonComponent from "../../components/ButtonComponent";
import ModalComponent from "../../components/ModalComponent";
import { SupplierService } from "../../services/SupplierService";
import { UpdateSupplierDTO } from "../../models/Supplier/UpdateSupplierDTO";
import { SupplierDTO } from "../../models/Supplier/SupplierDTO";
import Routes from "../../helpers/Routes";
import Toast from "../../helpers/Toast";
import { useIntl } from "react-intl";
import LoadingSpinner from "../../components/LoadingSpinner";

export default function SupplierEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [pageTitle, setPageTitle] = useState<string>(intl.formatMessage({ id: "Screens.Suppliers.Edit.Title" }));
  const [supplier, setSupplier] = useState<UpdateSupplierDTO>({
    id: 0,
    name: null,
    emailAddress: null,
    address: null,
    type: null,
    notes: null,
    phoneNumber: null,
    active: true
  });

  useEffect(() => {
    if (id) {
      fetchSupplier(parseInt(id));
    }
  }, [id]);

  const fetchSupplier = async (supplierId: number) => {
    setIsLoading(true);
    const supplierService = new SupplierService();
    const response = await supplierService.GetById(supplierId);

    if (response.success && response.obj) {
      const supplierData = response.obj as SupplierDTO;
      setSupplier({
        id: supplierData.id,
        name: supplierData.name,
        emailAddress: supplierData.emailAddress,
        address: supplierData.address,
        type: supplierData.type,
        notes: supplierData.notes,
        phoneNumber: supplierData.phoneNumber ? String(supplierData.phoneNumber) : null,
        active: supplierData.active
      });
      setPageTitle(`${intl.formatMessage({ id: "Screens.Suppliers.Edit.Title" })}: ${supplierData.name}`);
    } else {
      Toast.Show("error", response.message || intl.formatMessage({ id: "Screens.Suppliers.Edit.FetchError" }));
      navigate(Routes.suppliers.list);
    }
    setIsLoading(false);
  };

  const handleInputChange = (name: string, value: any) => {
    setSupplier({
      ...supplier,
      [name]: value
    });
  };

  const handleCheckboxChange = (checked: boolean) => {
    setSupplier({
      ...supplier,
      active: checked
    });
  };

  const handleSubmit = async () => {
    setIsSaving(true);

    const supplierService = new SupplierService();
    const response = await supplierService.Update(supplier);

    if (response.success) {
      Toast.Show("success", intl.formatMessage({ id: "Screens.Suppliers.Edit.Success" }));
      navigate(Routes.suppliers.list);
    } else {
      Toast.Show("error", response.message || intl.formatMessage({ id: "Screens.Suppliers.Edit.Error" }));
    }

    setIsSaving(false);
  };

  // Wrapper function for ButtonComponent
  const handleSaveClick = () => {
    handleSubmit();
  };

  const handleDelete = () => {
    setIsOpen(true);
  };

  const confirmDelete = async () => {
    setIsOpen(false);
    setIsSaving(true);
    const supplierService = new SupplierService();
    const response = await supplierService.Delete(supplier.id);

    if (response.success) {
      Toast.Show("success", intl.formatMessage({ id: "Screens.Suppliers.Edit.DeleteSuccess" }));
      navigate(Routes.suppliers.list);
    } else {
      Toast.Show("error", response.message || intl.formatMessage({ id: "Screens.Suppliers.Edit.DeleteError" }));
    }
    setIsSaving(false);
  };

  return (
    <Layout>
      <div className="pageContainer">
        <ModalComponent 
          IsOpen={isOpen} 
          text={intl.formatMessage({ id: "Screens.Suppliers.Edit.DeleteConfirmation" })} 
          OnClickRight={() => setIsOpen(false)} 
          OnClickLeft={confirmDelete} 
          title={intl.formatMessage({ id: "Screens.Suppliers.Form.Delete" })} 
          buttonRight={intl.formatMessage({ id: "Common.No" })} 
          buttonLeft={intl.formatMessage({ id: "Common.Yes" })} 
        />
        <div className="pageHeader">
              <PageTitle
                title={pageTitle}
                allowGoBack={true}
              />
        </div>

        <div className="pageBody">
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <div>
              <label style={{ margin: "0px", padding: "13px" }}>
                <b>{intl.formatMessage({ id: "Screens.Suppliers.Edit.FormTitle" })}</b>
              </label>
              <Row
                style={{
                  margin: "0px",
                  padding: "0px",
                  alignItems: "flex-end",
                  alignContent: "flex-end",
                }}
              >
                  <Row>
                    <InputComponent
                      xl={6}
                      lg={6}
                      md={6}
                      sm={12}
                      xs={12}
                      type="text"
                      label={intl.formatMessage({ id: "Screens.Suppliers.Form.Name" })}
                      value={supplier.name || ""}
                      onChange={(value) => handleInputChange("name", value)}
                      readOnly={false}
                    />
                    <InputComponent
                      xl={6}
                      lg={6}
                      md={6}
                      sm={12}
                      xs={12}
                      type="text"
                      label={intl.formatMessage({ id: "Screens.Suppliers.Form.Email" })}
                      value={supplier.emailAddress || ""}
                      onChange={(value) => handleInputChange("emailAddress", value)}
                      readOnly={false}
                    />
                  </Row>
                  <Row>
                    <InputComponent
                      xl={6}
                      lg={6}
                      md={6}
                      sm={12}
                      xs={12}
                      type="text"
                      label={intl.formatMessage({ id: "Screens.Suppliers.Form.Phone" })}
                      value={supplier.phoneNumber || ""}
                      onChange={(value) => handleInputChange("phoneNumber", value)}
                      readOnly={false}
                    />
                    <InputComponent
                      xl={6}
                      lg={6}
                      md={6}
                      sm={12}
                      xs={12}
                      type="text"
                      label={intl.formatMessage({ id: "Screens.Suppliers.Form.Type" })}
                      value={supplier.type || ""}
                      onChange={(value) => handleInputChange("type", value)}
                      readOnly={false}
                    />
                  </Row>
                  <Row>
                    <InputComponent
                      xl={12}
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      type="text"
                      label={intl.formatMessage({ id: "Screens.Suppliers.Form.Address" })}
                      value={supplier.address || ""}
                      onChange={(value) => handleInputChange("address", value)}
                      readOnly={false}
                    />
                  </Row>
                  <Row>
                    <InputComponent
                      xl={12}
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      type="textArea"
                      rows={4}
                      label={intl.formatMessage({ id: "Screens.Suppliers.Form.Notes" })}
                      value={supplier.notes || ""}
                      onChange={(value) => handleInputChange("notes", value)}
                      readOnly={false}
                    />
                  </Row>
                  <Row style={{ marginTop: "10px" }}>
                    <Col md={6} className="d-flex align-items-center">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="activeSwitch"
                          checked={supplier.active}
                          onChange={(e) => handleCheckboxChange(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="activeSwitch">
                          {intl.formatMessage({ id: "Screens.Suppliers.Form.Active" })}
                        </label>
                      </div>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: "20px" }}>
                    <Col xl={12} className="d-flex gap-2">
                      <ButtonComponent
                        text={isSaving 
                          ? intl.formatMessage({ id: "Common.Loading" }) 
                          : intl.formatMessage({ id: "Screens.Suppliers.Form.Update" })}
                        onClick={handleSaveClick}
                        loading={isSaving}
                        disabled={isSaving}
                      />
                      <ButtonComponent
                        text={intl.formatMessage({ id: "Screens.Suppliers.Form.Delete" })}
                        onClick={handleDelete}
                        disabled={isSaving}
                        className="button-danger"
                      />
                    </Col>
                  </Row>
              </Row>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
